import styled from '@emotion/styled'

import type { BridgeStatus, LogEntry } from './lib/types'

const STATUS_COLORS: Record<BridgeStatus, { bg: string; fg: string }> = {
  ready: { bg: '#e2f5e9', fg: '#0f7b43' },
  timeout: { bg: '#fbe4e0', fg: '#b42318' },
  connecting: { bg: '#fef6e0', fg: '#a15c00' },
}

const LOG_BG: Record<LogEntry['type'], string> = {
  error: '#fbe4e0',
  connect: '#e2f5e9',
  event: '#e0eefc',
  response: '#f5f7fa',
  info: '#fef6e0',
}

const LOG_FG: Record<LogEntry['type'], string> = {
  error: '#b42318',
  connect: '#0f7b43',
  event: '#0b6bcb',
  response: '#5c6675',
  info: '#a15c00',
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 16px calc(40px + var(--safe-bottom, 0px));
  background: #f5f7fa;
  min-height: 100dvh;
`

export const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #1c2430;
`

export const SubTitle = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  color: #5c6675;
`

export const StatusCard = styled.div<{ status: BridgeStatus }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ status }) => STATUS_COLORS[status].bg};
  color: ${({ status }) => STATUS_COLORS[status].fg};
`

export const StatusDot = styled.span<{ status: BridgeStatus }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ status }) => STATUS_COLORS[status].fg};
  animation: ${({ status }) => (status === 'connecting' ? 'pulse 1.2s infinite' : 'none')};

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
  }
`

export const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`
export const StatusLabel = styled.span`
  font-size: 11px;
  opacity: 0.7;
`
export const StatusValue = styled.span`
  font-size: 15px;
  font-weight: 700;
`

export const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`
export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e3e6ec;
  border-radius: 10px;
`
export const InfoLabel = styled.span`
  font-size: 11px;
  color: #5c6675;
`
export const InfoValue = styled.span<{ ok: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ ok }) => (ok ? '#0f7b43' : '#5c6675')};
`
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e3e6ec;
  border-radius: 12px;
`
export const LogSection = styled(Section)`
  position: sticky;
  top: 0;
  z-index: 10;
  border: 2px solid #0b6bcb;
  background: #f4f9ff;
`
export const Notice = styled.div`
  padding: 10px 12px;
  font-size: 12px;
  color: #5c6675;
  background: #fef6e0;
  border-left: 3px solid #a15c00;
  border-radius: 6px;
  line-height: 1.55;
`
export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: #1c2430;
`
export const Desc = styled.p`
  margin: 0;
  font-size: 12px;
  color: #5c6675;
`
export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`
export const SubLabel = styled.label`
  font-size: 11px;
  color: #5c6675;
  font-weight: 600;
`
export const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-family: inherit;
  &:focus {
    outline: 2px solid #0b6bcb;
    outline-offset: -1px;
  }
`

export const PrimaryButton = styled.button`
  padding: 10px 14px;
  background: #0b6bcb;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  &:disabled {
    background: #b6c2d0;
    cursor: not-allowed;
  }
`
export const ClearButton = styled.button`
  padding: 4px 10px;
  background: #f5f7fa;
  color: #5c6675;
  border-radius: 6px;
  font-size: 11px;
`

export const LogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px;
`
export const EmptyLog = styled.div`
  padding: 18px;
  text-align: center;
  font-size: 12px;
  color: #5c6675;
`
export const LogItem = styled.div<{ type: LogEntry['type'] }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ type }) => LOG_BG[type]};
`
export const LogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
export const LogTime = styled.span`
  font-size: 10px;
  color: #5c6675;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
`
export const LogType = styled.span<{ type: LogEntry['type'] }>`
  font-size: 10px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 3px;
  color: ${({ type }) => LOG_FG[type]};
  background: rgba(255, 255, 255, 0.6);
`
export const LogMessage = styled.div`
  font-size: 12px;
  color: #1c2430;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-all;
  white-space: pre-wrap;
`
